import RedoIcon from '@src/svgs/RedoIcon';
import SearchIcon from '@src/svgs/SearchIcon';
import ShareUpIcon from '@src/svgs/ShareUpIcon';
import UndoIcon from '@src/svgs/UndoIcon';
import { analytics } from '@src/utility/analytics';
import { handleCopyToClipboard } from '@src/utility/general';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import TranscriptVersionSelector from './TranscriptVersionSelector';

import type { TranscriptVersion } from '../../repositories/TranscriptRepository';
import type { TranscriptEditorStore } from '../TranscriptEditorStore';

interface TranscriptEditorHeaderProps {
  editorStore: TranscriptEditorStore;
  onClose?: () => void;
  onVersionConfirmationRequired: (version: TranscriptVersion) => void;
  standalone?: boolean;

  /**
   * Current search term used for filtering the transcript
   */
  searchTerm: string;

  /**
   * Callback fired whenever the user changes the search input
   */
  onSearchTermChange: (value: string) => void;
}

/**
 * Header component for the transcript editor with version selector and undo/redo buttons
 */
const TranscriptEditorHeader: React.FC<TranscriptEditorHeaderProps> = ({
  editorStore,
  onClose,
  onVersionConfirmationRequired,
  standalone = false,
  searchTerm,
  onSearchTermChange,
}) => {
  // State for copy tooltip and format menu
  const [copied, setCopied] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const formatMenuRef = useRef<HTMLDivElement>(null);
  
  // Get user identity and feature flags
  const userIdentity = useUserIdentity();
  const { enableTranscriptProviderSelector } = useFlagsCombination(userIdentity.currentRole);
  
  // Check if transcript version selector should be shown
  const showVersionSelector = enableTranscriptProviderSelector;

  // Handle copy transcript action with format options
  const handleCopyTranscript = (
    withSpeakers: boolean = true,
    withTimecodes: boolean = false,
  ) => {
    if (!editorStore.editor) return;

    // Format transcript with the selected options
    const formattedTranscript = editorStore.formatTranscript(
      withSpeakers,
      withTimecodes,
    );

    // Copy to clipboard
    handleCopyToClipboard(formattedTranscript);

    // Set copied state for UI feedback
    setCopied(true);

    // Hide format menu after copying
    setShowFormatMenu(false);

    // Reset copied state after 3 seconds
    setTimeout(() => {
      setCopied(false);
    }, 3000);

    // Track copy in analytics
    analytics.track('transcript_copied', {
      storyId: editorStore.editor.storyId,
      language: editorStore.language,
      format:
        withSpeakers && withTimecodes
          ? 'speakers_and_timecodes'
          : withSpeakers
            ? 'speakers'
            : withTimecodes
              ? 'timecodes'
              : 'plain',
    });
  };

  // Handle click outside to close format menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        formatMenuRef.current &&
        !formatMenuRef.current.contains(event.target as Node)
      ) {
        setShowFormatMenu(false);
      }
    };

    if (showFormatMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFormatMenu]);

  // Toggle format menu
  const toggleFormatMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFormatMenu(!showFormatMenu);
  };

  // Handle undo action
  const handleUndo = async () => {
    if (!editorStore.editor) return;

    if (editorStore.getUndoStackSize() > 0) {
      try {
        // Call the undo method on the store which handles the command manager interaction
        const result = await editorStore.undoLastOperation();

        if (result) {
          // Track undo in analytics
          analytics.track('transcript_command_undone', {
            storyId: editorStore.editor.storyId,
            language: editorStore.language,
          });

          // The observer will update the stack sizes automatically
        }
      } catch (error) {
        console.error('Error during undo:', error);
      }
    }
  };

  // Handle redo action
  const handleRedo = async () => {
    if (!editorStore.editor) return;

    if (editorStore.getRedoStackSize() > 0) {
      try {
        // Call the redo method on the store which handles the command manager interaction
        const result = await editorStore.redoLastOperation();

        if (result) {
          // Track redo in analytics
          analytics.track('transcript_command_redone', {
            storyId: editorStore.editor.storyId,
            language: editorStore.language,
          });

          // The observer will update the stack sizes automatically
        }
      } catch (error) {
        console.error('Error during redo:', error);
      }
    }
  };

  return (
    <Header>
      <SectionTitle>Transcript</SectionTitle>

      <ActionButtons>
        {/* Undo/Redo buttons */}
        <UndoRedoButtons>
          <IconButton
            onClick={handleUndo}
            title="Undo (Ctrl+Z)"
            disabled={editorStore.getUndoStackSize() === 0}
          >
            <UndoIcon
              width="16"
              height="16"
              strokeColor={
                editorStore.getUndoStackSize() > 0 ? '#F2D093' : '#6E6E6E'
              }
            />
          </IconButton>
          <UndoRedoDivider />
          <IconButton
            onClick={handleRedo}
            title="Redo (Ctrl+Shift+Z or Ctrl+Y)"
            disabled={editorStore.getRedoStackSize() === 0}
          >
            <RedoIcon
              width="16"
              height="16"
              strokeColor={
                editorStore.getRedoStackSize() > 0 ? '#F2D093' : '#6E6E6E'
              }
            />
          </IconButton>
        </UndoRedoButtons>

        {/* Share button with format options */}
        <ShareButtonContainer>
          <IconButton onClick={toggleFormatMenu} title="Copy transcript">
            <ShareUpIcon />
          </IconButton>
          {copied && <CopiedIndicator>Copied!</CopiedIndicator>}

          {/* Format options menu */}
          {showFormatMenu && (
            <FormatMenu ref={formatMenuRef}>
              <FormatOption onClick={() => handleCopyTranscript(false, false)}>
                Plain text
              </FormatOption>
              <FormatOption onClick={() => handleCopyTranscript(true, false)}>
                With speakers
              </FormatOption>
              <FormatOption onClick={() => handleCopyTranscript(false, true)}>
                With timecodes
              </FormatOption>
              <FormatOption onClick={() => handleCopyTranscript(true, true)}>
                With speakers and timecodes
              </FormatOption>
            </FormatMenu>
          )}
        </ShareButtonContainer>

        {/* Search bar */}
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search Transcript"
            value={searchTerm}
            onChange={e => onSearchTermChange(e.target.value)}
          />
          <SearchIcon fillColor="#F2D093" />
        </SearchContainer>

        {/* Version Selector - Only show if debug mode is enabled */}
        {showVersionSelector && (
          <LanguagePlaybackContainer>
            {editorStore.isViewingNonLatestVersion() && (
              <VersionWarning>
                <span>⚠️ Not on latest version</span>
              </VersionWarning>
            )}
            <TranscriptVersionSelector
              editorStore={editorStore}
              onConfirmationRequired={onVersionConfirmationRequired}
            />
          </LanguagePlaybackContainer>
        )}

        {standalone && onClose && <Button onClick={onClose}>Close</Button>}
      </ActionButtons>
    </Header>
  );
};

// Styled components
const Header = styled.div`
  grid-area: header;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 24px 16px;
  background-color: #030419;
  border-bottom: 1px solid #1a1e23;
  z-index: 100;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
`;

const LanguagePlaybackContainer = styled.div`
  display: flex;
  align-items: left;
  gap: 16px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SearchContainer = styled.div`
  width: 210px;
  padding-left: 16px;
  padding-right: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #484848;
  border-radius: 16px;
  transition: 0.2s ease;
  &:focus-within {
    border-color: #f2d093;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 8px 0;
  border: none;
  background: none;
  outline: none;
  color: #f3e9d7;
  font-size: 12px;
  font-weight: 500;

  ::placeholder {
    color: #484848;
  }
`;

// Undo/redo buttons container
const UndoRedoButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const UndoRedoDivider = styled.div`
  width: 1px;
  height: 16px;
  background-color: #3d3d3d;
`;

// Icon button for undo/redo
const IconButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background-color: transparent;
  border-radius: 4px;
  border: none;
  outline: none;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  padding: 0;
  opacity: ${props => (props.disabled ? 0.6 : 1)};

  &:hover {
    background-color: ${props =>
      props.disabled ? 'transparent' : 'rgba(242, 208, 147, 0.1)'};
  }

  &:active {
    transform: ${props => (props.disabled ? 'none' : 'scale(0.95)')};
  }
`;

const ShareButtonContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const CopiedIndicator = styled.div`
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #17c964;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  z-index: 1000;

  &:before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent #17c964 transparent;
  }
`;

// Format menu styling
const FormatMenu = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  background-color: #1e1e2c;
  border: 1px solid #4a4a6a;
  border-radius: 4px;
  width: 200px;
  z-index: 999;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const FormatOption = styled.div`
  padding: 8px 12px;
  color: #f3e9d7;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(242, 208, 147, 0.15);
  }

  &:not(:last-child) {
    border-bottom: 1px solid #2e2e3e;
  }
`;

const Button = styled.button<{ primary?: boolean; danger?: boolean }>`
  background-color: ${props => {
    if (props.primary) return '#f2d093';
    if (props.danger) return 'rgba(255, 105, 97, 0.2)';
    return 'transparent';
  }};
  color: ${props => {
    if (props.primary) return '#030419';
    if (props.danger) return '#ff6961';
    return '#f3e9d7';
  }};
  border: 1px solid
    ${props => {
      if (props.primary) return '#f2d093';
      if (props.danger) return '#ff6961';
      return '#484848';
    }};
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => {
      if (props.primary) return '#d9b77e';
      if (props.danger) return 'rgba(255, 105, 97, 0.3)';
      return 'rgba(242, 208, 147, 0.1)';
    }};
  }
`;

const VersionWarning = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
  margin-left: 8px;
  height: 26px;
  background-color: rgba(242, 208, 147, 0.15);
  border-radius: 4px;

  span {
    font-size: 12px;
    color: #f2d093;
    white-space: nowrap;
  }
`;

export default TranscriptEditorHeader;
